import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api, ApiError, type Me } from '../../lib/api'
import { layout } from '../../design/tokens'
import { Cadenas } from '../items/Verrou'
import {
  activerLesNotifications,
  desactiverLesNotifications,
  etatDesNotifications,
  type EtatDesNotifications,
} from '../../lib/notifications'

/**
 * Mon compte — définir ou changer son mot de passe.
 *
 * Un compte né par lien magique n'a pas de mot de passe : il n'a alors rien à confirmer, la
 * session authentifiée prouve déjà l'identité. Dès qu'il en a un, l'ancien est exigé — sinon un
 * cookie volé permettrait de verrouiller le compte de son propriétaire.
 */
export function AccountSheet({ me, onClose }: { me: Me; onClose: () => void }) {
  const [actuel, setActuel] = useState('')
  const [nouveau, setNouveau] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [visible, setVisible] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  // Sans fournisseur d'e-mail (point ouvert P2), il n'existe aucune récupération : une faute de
  // frappe sur un champ unique enferme dehors définitivement. D'où la confirmation, et la
  // possibilité d'afficher ce qu'on tape.
  const discordant = confirmation.length > 0 && confirmation !== nouveau

  const enregistrer = useMutation({
    mutationFn: () => api.changePassword(actuel, nouveau),
    onSuccess: () =>
      setMessage(
        'Mot de passe enregistré. Toutes les sessions ont été fermées, reconnectez-vous avec.',
      ),
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'enregistrement a échoué."),
  })

  return (
    <div
      className="fixed inset-0 z-10 flex items-end justify-center"
      style={{ background: 'rgb(26 29 27 / 0.35)' }}
    >
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0" tabIndex={-1} />

      <section
        className="relative w-full rounded-sheet bg-surface p-4 shadow-sheet"
        style={{
          maxWidth: layout.column,
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
          animation: 'monter var(--duration-sheet) ease-out',
        }}
      >
        <NomAffiche me={me} />

        {/* Le code des fiches protégées se change ici aussi : le chercher sur une fiche
            verrouillée obligerait à en ouvrir une pour y arriver. */}
        <Link
          to="/code-confidentiel"
          className="mt-6 flex items-center gap-2 rounded-field border border-border bg-card p-3 text-body"
          style={{ textDecoration: 'none' }}
        >
          <Cadenas />
          {me.hasPin ? 'Changer mon code confidentiel' : 'Créer mon code confidentiel'}
          <span className="ml-auto text-ink-muted">›</span>
        </Link>

        <Notifications />

        <h2 className="mt-6 font-serif text-record">
          {me.hasPassword ? 'Changer mon mot de passe' : 'Définir un mot de passe'}
        </h2>
        <p className="mt-2 text-meta text-ink-muted">
          {me.hasPassword
            ? 'Vous vous connecterez avec votre e-mail et ce mot de passe.'
            : "Une fois défini, vous n'aurez plus besoin du lien envoyé par e-mail."}
        </p>

        {me.hasPassword ? (
          <>
            <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="actuel">
              Mot de passe actuel
            </label>
            <input
              id="actuel"
              type="password"
              autoComplete="current-password"
              value={actuel}
              onChange={(event) => {
                setActuel(event.target.value)
                setErreur(null)
              }}
              className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
              style={{ height: 56, fontSize: 17 }}
            />
          </>
        ) : null}

        <div className="mt-4 flex items-baseline justify-between">
          <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="nouveau">
            {me.hasPassword ? 'Nouveau mot de passe' : 'Mot de passe'}
          </label>
          <button
            type="button"
            onClick={() => setVisible((montre) => !montre)}
            className="text-meta font-semibold text-accent"
          >
            {visible ? 'Masquer' : 'Afficher'}
          </button>
        </div>
        <input
          id="nouveau"
          type={visible ? 'text' : 'password'}
          autoComplete="new-password"
          value={nouveau}
          onChange={(event) => {
            setNouveau(event.target.value)
            setErreur(null)
          }}
          className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
          style={{ height: 56, fontSize: 17 }}
        />
        <p className="mt-2 text-meta text-ink-muted">Au moins 10 caractères.</p>

        <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="confirmation">
          Retapez-le
        </label>
        <input
          id="confirmation"
          type={visible ? 'text' : 'password'}
          autoComplete="new-password"
          value={confirmation}
          onChange={(event) => {
            setConfirmation(event.target.value)
            setErreur(null)
          }}
          aria-invalid={discordant}
          className="mt-2 w-full rounded-field border-[1.5px] bg-card px-4 outline-none"
          style={{
            height: 56,
            fontSize: 17,
            borderColor: discordant ? 'var(--color-danger)' : 'var(--color-accent)',
          }}
        />
        {discordant ? (
          <p className="mt-2 text-meta" style={{ color: 'var(--color-danger)' }}>
            Les deux saisies diffèrent.
          </p>
        ) : null}

        {erreur ? (
          <p className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
            {erreur}
          </p>
        ) : null}
        {message ? <p className="mt-3 text-meta text-accent">{message}</p> : null}

        <button
          type="button"
          disabled={
            nouveau.length < 10 ||
            confirmation !== nouveau ||
            (me.hasPassword && actuel.length === 0) ||
            enregistrer.isPending ||
            message !== null
          }
          onClick={() => enregistrer.mutate()}
          className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
          style={{ height: layout.primaryButtonHeight }}
        >
          Enregistrer
        </button>

        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
          style={{ height: layout.primaryButtonHeight }}
        >
          Fermer
        </button>
      </section>
    </div>
  )
}

/**
 * Son propre nom affiché.
 *
 * L'administration en pose un à la création — souvent « Prénom Nom » — mais c'est l'intéressé qui
 * décide de la façon dont on le nomme dans l'application.
 */
function NomAffiche({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const [nom, setNom] = useState(me.displayName)
  const [message, setMessage] = useState<string | null>(null)

  const renommer = useMutation({
    mutationFn: () => api.renameMe(nom.trim()),
    onSuccess: async () => {
      setMessage('Nom enregistré.')
      await queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : 'Le changement a échoué.'),
  })

  return (
    <>
      <h2 className="font-serif text-record">Mon nom</h2>
      <div className="mt-3 flex gap-2">
        <input
          aria-label="Nom affiché"
          value={nom}
          onChange={(evenement) => {
            setNom(evenement.target.value)
            setMessage(null)
          }}
          className="grow rounded-field border border-border bg-card px-4 outline-none"
          style={{ height: 48, fontSize: 16 }}
        />
        <button
          type="button"
          disabled={nom.trim().length === 0 || nom.trim() === me.displayName || renommer.isPending}
          onClick={() => renommer.mutate()}
          className="rounded-button bg-accent px-4 text-meta font-semibold text-card disabled:opacity-40"
          style={{ height: 48 }}
        >
          Enregistrer
        </button>
      </div>
      {message ? <p className="mt-2 text-meta text-ink-muted">{message}</p> : null}
    </>
  )
}

/**
 * Les notifications de cet appareil.
 *
 * « Sur cet appareil » n'est pas une coquetterie : l'autorisation et l'abonnement sont délivrés
 * par le navigateur, donc ils valent pour le téléphone ou pour l'ordinateur, jamais pour le
 * compte. Les activer ici ne les active pas ailleurs, et c'est ce que dit le libellé.
 *
 * L'état est relu à chaque ouverture plutôt que mémorisé : l'autorisation se révoque dans les
 * réglages du navigateur, sans que l'application en soit avertie.
 */
function Notifications() {
  const [etat, setEtat] = useState<EtatDesNotifications | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)

  const { data: cle } = useQuery({ queryKey: ['push-cle'], queryFn: api.pushKey })

  useEffect(() => {
    void etatDesNotifications().then(setEtat)
  }, [])

  const basculer = async () => {
    setOccupe(true)
    setMessage(null)

    try {
      if (etat === 'actif') {
        const adresse = await desactiverLesNotifications()
        await api.unsubscribePush(adresse ?? undefined)
        setEtat('inactif')

        return
      }

      if (!cle?.publicKey) {
        setMessage("Le serveur n'est pas configuré pour les notifications.")

        return
      }

      const abonnement = await activerLesNotifications(cle.publicKey)

      if (abonnement === null) {
        setEtat(await etatDesNotifications())
        setMessage("L'autorisation n'a pas été donnée.")

        return
      }

      await api.subscribePush(abonnement)
      setEtat('actif')
      setMessage('Vous recevrez le rappel du matin sur cet appareil.')
    } catch (cause: unknown) {
      setMessage(cause instanceof ApiError ? cause.message : "L'opération a échoué.")
    } finally {
      setOccupe(false)
    }
  }

  // Rien à proposer : navigateur trop ancien, mode privé, ou serveur sans clés.
  if (etat === null || etat === 'indisponible' || !cle?.publicKey) {
    return null
  }

  return (
    <section className="mt-6">
      <h2 className="font-serif text-record">Notifications</h2>
      <p className="mt-2 text-meta text-ink-muted">
        Un rappel le matin, quand la journée a quelque chose. Rien quand elle est vide.
      </p>

      {etat === 'refuse' ? (
        // Un refus ne se reprend pas depuis l'application : seul le navigateur peut le lever.
        <p className="mt-3 text-meta text-ink-muted">
          Vous les avez refusées pour ce site. Pour les rétablir, autorisez les notifications
          dans les réglages du navigateur.
        </p>
      ) : (
        <button
          type="button"
          disabled={occupe}
          onClick={() => void basculer()}
          className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
          style={{ height: layout.primaryButtonHeight }}
        >
          {occupe
            ? 'Un instant…'
            : etat === 'actif'
              ? 'Désactiver sur cet appareil'
              : 'Activer sur cet appareil'}
        </button>
      )}

      {message ? <p className="mt-2 text-meta text-ink-muted">{message}</p> : null}
    </section>
  )
}
